import { Declaration, Example } from "./types";

function getDeclarationFromString(css: string): Declaration[] {
  return css
    .split("\n")
    .filter((line) => line !== "")
    .map((line) => {
      const lineSplits = line.split(/(: )|(;)/g);

      const splits = lineSplits
        .filter((split) => {
          if (!split) {
            return false;
          }

          if (split === ": " || split === ";") {
            return false;
          }

          return true;
        })
        .map((split) => split.trim());

      return splits;
    })
    .filter((splits) => splits.length >= 2)
    .map((splits) => {
      const [name, value] = splits;

      return {
        name,
        value,
        enabled: true,
      };
    });
}

export function declarationsToCSSString(
  declarations: Declaration[],
  parentClass: string
): string {
  const allDeclarations = declarations
    .map(
      (declaration) =>
        `${declaration.name}: ${
          !declaration.enabled ? "unset" : declaration.value
        };`
    )
    .join("\n");

  return `
    .${parentClass} {
      ${allDeclarations}
    }
  `;
}

const italic: Example = {
  name: "Italic",
  iframeUrl: "/examples/italic.com",
  defaultParentClassname: "collections-grid",
  declarations: getDeclarationFromString(`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: 1.5rem;
    padding-bottom: 4rem;
  `),
};

const gridMasterclass: Example = {
  name: "Masterclass",
  iframeUrl: "/examples/grid-masterclass",
  declarations: getDeclarationFromString(`
    align-content: start;
    grid-column-gap: 0px;
    grid-row-gap: 0px;
    grid-template-columns: 1fr 1fr 1fr 25%;
    grid-template-rows: 1fr 1fr 1fr 0.5fr;
  `),
  defaultParentClassname: "grid",
  htmlOutput: `
  <div class="grid">
    <div class="div-block-7"></div>
    <div class="div-block-4"></div>
    <div class="div-block-3"></div>
    <div class="div-block-5"></div>
    <div class="div-block-6"></div>
    <div class="div-block"></div>
    <div class="div-block-8"></div>
    <div class="div-block-4"></div>
    <div class="div-block-10"></div>
    <div class="div-block-2"></div>
  </div>
  `,
  children: [
    `
    .div-block-4 {
      grid-column-start: 3;
      grid-column-end: 5;
      grid-row-start: 4;
      grid-row-end: 5;
    }
    `,
    `
    .div-block-4 {
      grid-column-start: 2;
      grid-column-end: 3;
      grid-row-start: 4;
      grid-row-end: 5;
    }
    `,
    `
    .div-block-3 {
      grid-column-start: 4;
      grid-column-end: 5;
      grid-row-start: 2;
      grid-row-end: 4;
    }
    `,
    `
    .div-block-5 {
      grid-column-start: 2;
      grid-column-end: 4;
      grid-row-start: 3;
      grid-row-end: 4;
    }
    `,
    `
    .div-block-6 {
      grid-column-start: 2;
      grid-column-end: 3;
      grid-row-start: 3;
      grid-row-end: 4;
    }
    `,
    `
    .div-block {
      grid-column-start: 1;
      grid-column-end: 2;
      grid-row-start: 4;
      grid-row-end: 5;
    }
    `,
    `
    .div-block-8 {
      grid-column-start: 3;
      grid-column-end: 5;
      grid-row-start: 1;
      grid-row-end: 2;
    }
    `,
    `
    .div-block-4 {
      grid-column-start: 4;
      grid-column-end: 5;
      grid-row-start: 1;
      grid-row-end: 2;
    }
    `,
    `
    .div-block-10 {
      grid-column-start: 3;
      grid-column-end: 4;
      grid-row-start: 1;
      grid-row-end: 2;
    }
    `,
  ],
};

const flatIcons: Example = {
  name: "Flat Icons",
  iframeUrl: "/examples/flaticons.com",
  declarations: getDeclarationFromString(`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    align-content: start;
    width: 100%;
  `),
  defaultParentClassname: "icons",
};

const smashingMagazine: Example = {
  name: "Smashing Magazine",
  iframeUrl: "/examples/smashingmagazine.com",
  declarations: getDeclarationFromString(`
    display: flex;
    flex-wrap: wrap;
  `),
  defaultParentClassname: "f-article-highlights",
  htmlOutput: `
  <div class="f-article-highlights article--grid__container col col--slim col-12">
    <div class="f-article-item">Article 1</div>
    <div class="f-article-item">Article 2</div>
    <div class="f-article-item">Article 3</div>
    <div class="f-article-item">Article 4</div>
    <div class="f-article-item">Article 5</div>
    <div class="f-article-item">Article 6</div>
  </div>
  `,
  children: [
    `
.f-article-item {
  flex-basis: 50%;
}
`,
  ],
};

const csstricks: Example = {
  name: "CSS Tricks",
  iframeUrl: "/examples/css-tricks.com",
  declarations: getDeclarationFromString(`
    box-sizing: content-box;
    display: flex;
    margin: 0 0 var(--gap);
    position: relative;
  `),
  defaultParentClassname: "popular-articles",
  htmlOutput: `
<div class="popular-articles">
  <div class="popular-header"></div>

  <div class="mini-card-grid">
    <div class="mini-card"></div>
    <div class="mini-card"></div>
    <div class="mini-card"></div>
    <div class="mini-card"></div>
    <div class="mini-card"></div>
    <div class="mini-card"></div>
    <div class="mini-card"></div>
    <div class="mini-card"></div>
    <div class="mini-card"></div>
    <div class="mini-card"></div>
  </div>
</div>
  `,
  children: [
    `
.mini-card {
    min-width: 300px;
    min-height: 350px;
    box-shadow: -2rem 0 3rem -2rem #000;
}

.mini-card:not(:first-child) {
  margin-left: -130px;
  box-shadow: -3rem 0 3rem -2rem #000;
}
`,
  ],
};

const heroicons: Example = {
  name: "Hero Icons",
  iframeUrl: "/examples/heroicons.com",
  declarations: getDeclarationFromString(`
    display: grid;
    gap: 4rem;
    text-align: center;
    font-size: 0.8rem;
    grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  `),
  defaultParentClassname: "icon-grid",
};

export const examples: Example[] = [
  italic,
  gridMasterclass,
  flatIcons,
  smashingMagazine,
  csstricks,
  heroicons,
];
